/**
 * Permission structure from backend GraphQL
 */
export interface Permission {
  resource: string
  action: string
  permission_code: string
}

/**
 * CASL rule returned per-profile from the backend.
 * Fed directly into createMongoAbility() for access control.
 */
export interface CaslRule {
  action: string
  subject: string
}

/**
 * User profile representing different roles (Organization, Operation, Provider, Client)
 * Matches backend ProfilePermissionsType from accounts/schema.py
 */
export interface UserProfile {
  profile_id: string
  profile_type: 'CLIENT' | 'PROVIDER' | 'OPERATION' | 'ORGANIZATION' | 'ENTITY' | 'BUSINESS_CLIENT' | 'BENEFICIARY' | 'UNKNOWN'
  display_name: string
  is_primary: boolean
  business_account_id?: string | null
  business_account?: {
    id: string
    is_active?: boolean | null
  } | null
  organization_id?: string
  organization_name?: string
  permissions?: Permission[]
  permission_codes: string[]
  groups?: string[]
  casl_rules?: CaslRule[]
}

/**
 * Admin permissions from backend GraphQL
 */
export interface AdminPermissions {
  is_staff: boolean
  is_superuser: boolean
  django_permissions: string[]
}

/**
 * Permission codes reference for the dashboard
 * Based on backend role_permissions in accounts/schema.py
 */
export const PERMISSION_CODES = {
  // Client permissions
  ORDERS_CREATE: 'orders.create',
  ORDERS_VIEW_OWN: 'orders.view_own',
  ORDERS_UPDATE_OWN: 'orders.update_own',
  ORDERS_CANCEL_OWN: 'orders.cancel_own',
  BENEFICIARIES_MANAGE: 'beneficiaries.manage',
  PROFILE_VIEW_OWN: 'profile.view_own',
  PROFILE_UPDATE_OWN: 'profile.update_own',
  
  // Provider permissions
  ORDERS_VIEW_ASSIGNED: 'orders.view_assigned',
  ORDERS_UPDATE_ASSIGNED: 'orders.update_assigned',
  ORDERS_ACCEPT: 'orders.accept',
  ORDERS_COMPLETE: 'orders.complete',
  AVAILABILITY_MANAGE: 'availability.manage',
  
  // Operation permissions
  ORDERS_VIEW_ALL: 'orders.view_all',
  ORDERS_MANAGE_ALL: 'orders.manage_all',
  PROVIDERS_MANAGE: 'providers.manage',
  ORGANIZATIONS_MANAGE: 'organizations.manage',
  USERS_VIEW_ALL: 'users.view_all',
  REPORTS_VIEW: 'reports.view',
  SYSTEM_CONFIGURE: 'system.configure',
  
  // Organization permissions
  ORDERS_VIEW_ORGANIZATION: 'orders.view_organization',
  PROVIDERS_MANAGE_ORGANIZATION: 'providers.manage_organization',
  REPORTS_VIEW_ORGANIZATION: 'reports.view_organization',
  ORGANIZATION_MANAGE_OWN: 'organization.manage_own',
  
  // Admin permission (for superusers)
  ADMIN_FULL_ACCESS: 'admin.full_access',
} as const

export type PermissionCode = typeof PERMISSION_CODES[keyof typeof PERMISSION_CODES]

/**
 * User interface matching backend UserType from accounts/schema.py
 */
export interface User {
  id: string
  user_id?: string
  first_name: string
  last_name: string
  phone_number: string
  email?: string
  name?: string
  role?: string
  is_active?: boolean
  is_staff?: boolean
  is_superuser?: boolean
  groups?: string[]
  permissions?: string[]
  profiles?: UserProfile[]
  admin_permissions?: AdminPermissions
  // Flat list of all permission codes from active profile (computed on frontend)
  permission_codes?: string[]
}

/**
 * GraphQL response type for the 'me' query
 * Matches the backend UserType schema
 */
export interface MeQueryResponse {
  me: {
    id: string
    userId: string
    name: string
    firstName: string
    lastName: string
    phoneNumber: string
    email: string | null
    isActive: boolean
    isStaff: boolean
    isSuperuser: boolean
    role: string
    groups: string[]
    permissions: string[]
    profiles: Array<{
      profileId: string
      profileType: string
      displayName: string
      isPrimary: boolean
      businessAccountId?: string | null
      permissions: Array<{
        resource: string
        action: string
        permissionCode: string
      }>
      permissionCodes: string[]
      groups?: string[]
      caslRules?: Array<{
        action: string
        subject: string
      }>
    }>
    adminPermissions: {
      isStaff: boolean
      isSuperuser: boolean
      djangoPermissions: string[]
    } | null
  } | null
}

export interface AuthSession {
  session_id: string
  identifier: string
  strategy?: string
  requires_strategy_selection?: boolean
  available_strategies?: Strategy[]
  expires_at?: string
  session_duration_seconds?: number
}

export interface Strategy {
  strategy: string
  display_name: string
  description: string
  requirements: Record<string, any>
}

export interface PendingChallenge {
  session_id: string
  identifier: string
  strategy?: string
  challenge_id?: string // Challenge ID from generateOTP response
}

export interface AuthTokens {
  access: string
  refresh: string
}

export interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  pendingChallenge: PendingChallenge | null
  authSession: AuthSession | null
  loading: boolean
  error: string | null
}

export interface BeginAuthResponse {
  success: boolean
  session_id: string
  strategy?: string
  requires_strategy_selection?: boolean
  available_strategies?: Strategy[]
  requirements?: Record<string, any>
  total_steps?: number
  current_step?: number
  expires_at?: string
  session_duration_seconds?: number
}

export interface VerifyResponse {
  success: boolean
  user_id?: string
  requires_additional_step?: boolean
  completed_steps?: number
  total_steps?: number
  authentication_complete?: boolean
  access_token?: string
  refresh_token?: string
  token_type?: string
  session_data?: {
    challenge_id?: string
    expires_at?: string
    max_attempts?: number
  }
  session_id?: string
  expires_at?: string
}

export interface BeginAuthRequest {
  operation: 'login'
  identifier: string
}

export interface SelectStrategyRequest {
  session_id: string
  selected_strategy: string // Backend expects 'selected_strategy' not 'strategy'
}

export interface VerifyRequest {
  session_id: string
  identifier: string
  credential: string
  metadata?: Record<string, any>
}

// Legacy types for backward compatibility
export interface LoginResponse {
  session_id: string
  phone_number?: string
  identifier?: string
}

export interface VerifyOTPResponse {
  access: string
  refresh: string
  access_token?: string
  refresh_token?: string
  user?: User
}
